import { useEffect, useState } from "react";
import api from "../lib/api";
import { Percent } from "lucide-react";

const pct = (v) => (v == null ? "—" : `${v}%`);
const num = (v) => (v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }));
const tone = (v) => (v == null ? "text-[#1C2530]/40" : v >= 40 ? "text-[#2F7A3E]" : v >= 20 ? "text-[#8a6a12]" : "text-[#B23A2E]");

function Tile({ label, value, sub, testId }) {
  return (
    <div className="border border-[#DFDAD0] bg-white px-4 py-3" data-testid={testId}>
      <div className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">{label}</div>
      <div className={`text-xl font-semibold mt-0.5 ${tone(value)}`}>{pct(value)}</div>
      {sub && <div className="font-mono text-[10px] text-[#1C2530]/50 mt-0.5">{sub}</div>}
    </div>
  );
}

function flattenLadder(ladder) {
  const out = [];
  const walk = (parentId, depth) => {
    ladder.filter((n) => (n.parent_id || null) === parentId).forEach((n) => { out.push({ ...n, depth }); walk(n.node_id, depth + 1); });
  };
  walk(null, 0);
  return out;
}

function Spark({ trend, testId }) {
  const pts = (trend || []).map((t) => t.avg_margin_pct);
  const vals = pts.filter((v) => v != null);
  if (vals.length < 1) return <span className="font-mono text-[9px] text-[#1C2530]/30 w-20 text-center" data-testid={testId}>no trend</span>;
  const w = 80, h = 20, max = Math.max(100, ...vals), min = 0;
  const x = (i) => (i / Math.max(1, pts.length - 1)) * (w - 4) + 2;
  const y = (v) => h - 2 - ((v - min) / (max - min || 1)) * (h - 4);
  const path = pts.map((v, i) => (v == null ? null : `${x(i)},${y(v)}`)).filter(Boolean);
  const last = vals[vals.length - 1], prev = vals.length > 1 ? vals[vals.length - 2] : null;
  const col = prev == null || last === prev ? "#565E68" : last > prev ? "#2F7A3E" : "#B23A2E";
  return (
    <svg width={w} height={h} className="shrink-0" data-testid={testId} aria-label={`margin trend ${pts.map((v) => (v == null ? "–" : v + "%")).join(" → ")}`}>
      <title>{(trend || []).map((t) => `${t.month}: ${t.avg_margin_pct == null ? "—" : t.avg_margin_pct + "%"} (${t.proposals})`).join("\n")}</title>
      {path.length > 1 && <polyline points={path.join(" ")} fill="none" stroke={col} strokeWidth="1.5" />}
      {pts.map((v, i) => (v == null ? null : <circle key={i} cx={x(i)} cy={y(v)} r="1.8" fill={col} />))}
    </svg>
  );
}

function LadderRow({ n }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2 border-t border-[#DFDAD0]" style={{ paddingLeft: 16 + n.depth * 20 }} data-testid={`margin-node-${n.node_id}`}>
      <div className="flex-1 min-w-0">
        <span className="text-sm text-[#1C2530] font-medium">{n.name}</span>
        <span className="font-mono text-[10px] text-[#1C2530]/50 ml-2">{n.kind || "unit"} · {n.rep_count} rep{n.rep_count === 1 ? "" : "s"}{n.heads.length ? ` · head: ${n.heads.join(", ")}` : ""}</span>
      </div>
      <Spark trend={n.trend} testId={`margin-trend-${n.node_id}`} />
      <span className="font-mono text-[10px] text-[#1C2530]/50">{n.proposals} proposals · net {num(n.net_total)}</span>
      <span className={`font-mono text-sm font-semibold w-16 text-right ${tone(n.avg_margin_pct)}`}>{pct(n.avg_margin_pct)}</span>
    </div>
  );
}

export default function MarginLadder() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/productivity/margin").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (d === false || !d) return null;
  const rows = flattenLadder(d.ladder);
  return (
    <div className="mb-6 space-y-3" data-testid="margin-ladder">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60"><Percent className="w-3.5 h-3.5" /> Gross margin · company → ladder → rep ({d.currency})</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="Company gross margin" value={d.company.avg_margin_pct} sub={`${d.company.proposals} proposals · margin ${num(d.company.margin_total)} on net ${num(d.company.net_total)}`} testId="margin-company" />
        <div className="border border-[#DFDAD0] bg-white px-4 py-3 flex items-center justify-between gap-3" data-testid="margin-company-trend">
          <div><div className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">Company · last 6 months</div><div className="font-mono text-[10px] text-[#1C2530]/50 mt-1">{(d.company.trend || []).map((t) => `${t.month.slice(5)} ${t.avg_margin_pct == null ? "—" : t.avg_margin_pct + "%"}`).join(" · ")}</div></div>
          <Spark trend={d.company.trend} testId="margin-company-spark" />
        </div>
        <Tile label="Best rep" value={d.reps[0]?.avg_margin_pct} sub={d.reps[0]?.name} testId="margin-best-rep" />
        <Tile label="Reps with proposals" value={null} sub={`${d.reps.filter((r) => r.proposals > 0).length} of ${d.reps.length}`} testId="margin-rep-coverage" />
      </div>
      {d.ladder.length > 0 ? (
        <div className="border border-[#DFDAD0] bg-white">
          <div className="px-4 py-2 font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">Manpower ladder · weighted average margin of everyone under each unit</div>
          {rows.map((n) => <LadderRow key={n.node_id} n={n} />)}
        </div>
      ) : (
        <div className="text-xs text-[#1C2530]/50 italic" data-testid="margin-ladder-empty">No org units yet — define the manpower ladder under Organization & Roles to see Sales Head / Vertical Head roll-ups.</div>
      )}
    </div>
  );
}
